import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, debounceTime, distinctUntilChanged, of, pipe, switchMap, tap } from 'rxjs';
import { InvoiceDataService } from '../../invoice-data.service';
import {
    IOrderCandidate,
    IOrderCandidateFilter,
    IOrderCandidateHeader,
    IOrderCandidateItem,
} from '../../types/IOrderCandidate';

interface LinkOrderDialogState {
    invoiceId: string;
    /** Head on screen in the edit mode, the only host of the dialog; {@code null} searches by the stored invoice. */
    header: IOrderCandidateHeader | null;
    /** Approval task the editor was opened from; {@code null} outside a task. */
    taskId: string | null;
    /** Current filter; named apart from the search() method — a signal store may not carry both. */
    searchFilter: IOrderCandidateFilter;
    searching: boolean;
    candidates: IOrderCandidate[];
    /** Order whose items are currently unfolded; only one at a time keeps the list readable. */
    expandedOrderId: string | null;
    selectedOrderId: string | null;
    selectedItem: IOrderCandidateItem | null;
}

export const EMPTY_ORDER_CANDIDATE_FILTER: IOrderCandidateFilter = {
    term: '',
    orderDateFrom: null,
    orderDateTo: null,
    includeClosed: false,
};

const initialState: LinkOrderDialogState = {
    invoiceId: '',
    header: null,
    taskId: null,
    searchFilter: EMPTY_ORDER_CANDIDATE_FILTER,
    searching: false,
    candidates: [],
    expandedOrderId: null,
    selectedOrderId: null,
    selectedItem: null,
};

/**
 * State of the manual order-linking dialog.
 *
 * The store only searches and remembers the selection — writing the relation is left to the caller,
 * so the dialog stays usable from any place that knows the invoice item.
 */
export const LinkOrderDialogStore = signalStore(
    withState(initialState),
    withMethods((store, invoiceDataService = inject(InvoiceDataService)) => ({
        init(invoiceId: string, header: IOrderCandidateHeader | null = null, taskId: string | null = null): void {
            patchState(store, { invoiceId, header, taskId });
            this.search(EMPTY_ORDER_CANDIDATE_FILTER);
        },

        search: rxMethod<IOrderCandidateFilter>(
            pipe(
                tap((filter) => patchState(store, { searchFilter: filter, searching: true })),
                debounceTime(300),
                // A filter is a fresh object on every keystroke, so identity comparison would never
                // suppress anything; compare the values that make up the query.
                distinctUntilChanged(
                    (a, b) =>
                        a.term === b.term &&
                        a.orderDateFrom === b.orderDateFrom &&
                        a.orderDateTo === b.orderDateTo &&
                        a.includeClosed === b.includeClosed,
                ),
                switchMap((filter) =>
                    invoiceDataService
                        .searchOrderCandidates(store.invoiceId(), filter, store.header(), store.taskId())
                        .pipe(
                            tap((candidates) => patchState(store, { candidates, searching: false })),
                            // catchError, not tap({error}): the error has to be swallowed here, otherwise the
                            // rxMethod stream completes and every later keystroke is lost.
                            catchError(() => {
                                patchState(store, { candidates: [], searching: false });
                                return of([] as IOrderCandidate[]);
                            }),
                        ),
                ),
            ),
        ),

        toggleOrder(orderId: string): void {
            patchState(store, { expandedOrderId: store.expandedOrderId() === orderId ? null : orderId });
        },

        selectItem(orderId: string, item: IOrderCandidateItem): void {
            const alreadySelected = store.selectedItem()?.orderItemId === item.orderItemId;
            patchState(store, {
                selectedOrderId: alreadySelected ? null : orderId,
                selectedItem: alreadySelected ? null : item,
            });
        },
    })),
);
